import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Injector,
  OnDestroy,
  OnInit,
  Renderer2,
  TemplateRef,
  ViewChild,
  afterNextRender,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';

import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatSidenav, MatSidenavContainer, MatSidenavContent } from '@angular/material/sidenav';

import { FsConnectionService } from '@firestitch/connection';
import { BodyClassRenderer } from '@firestitch/html';
import { FsSidenavModule, Item, ItemActionType, SidenavConfig } from '@firestitch/sidenav';
import { FsStore } from '@firestitch/store';

import { Subject, of, timer } from 'rxjs';
import { catchError, filter, finalize, skip, switchMap, takeUntil } from 'rxjs/operators';

import { DonateService } from '@libs/donation-invoices/donate/services';

import { ConversationsService } from '@app/conversations/services';
import { AclService, ExperienceService, OrganizationListService, SessionService } from '@app/core/services';
import { AllReportPermissions } from '@common/consts';
import { AccountData } from '@common/data';
import { AclPermission, Experience, FeatureFlag } from '@common/enums';
import { Account, Environment, Organization } from '@common/interfaces';

import { LogoComponent } from '../../../../logo/components/logo/logo.component';
import { SidenavService } from '../../services/sidenav.service';
import { EnvironmentMenuComponent } from '../environment-menu/environment-menu.component';
import { NavbarComponent } from '../navbar/navbar.component';
import { OrganizationExperienceMenuComponent } from '../organization-experience-menu';
import { ParentMenuComponent } from '../parent-menu/parent-menu.component';
import { TopBarComponent } from '../top-bar/top-bar.component';


@Component({
  selector: 'app-page-side-nav',
  templateUrl: './side-nav.component.html',
  styleUrls: ['./side-nav.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ConversationsService, OrganizationListService],
  standalone: true,
  imports: [
    CdkScrollable,
    MatSidenavContainer,
    MatSidenav,
    RouterLink,
    LogoComponent,
    FsSidenavModule,
    MatSidenavContent,
    NavbarComponent,
    RouterOutlet,
    TopBarComponent,
  ],
})
export class SideNavComponent implements OnInit, OnDestroy {
  sidenavService = inject(SidenavService);
  router = inject(Router);
  accountData = inject(AccountData);
  public readonly sideNavExperienceEl = viewChild.required('sideNavExperienceEl', { read: ElementRef });
  private _store = inject(FsStore);
  private _aclService = inject(AclService);
  private _bodyClassRenderer = inject(BodyClassRenderer);
  private _renderer = inject(Renderer2);
  private _document = inject(DOCUMENT);
  private _experienceService = inject(ExperienceService);
  private _donateService = inject(DonateService);
  private _cdRef = inject(ChangeDetectorRef);
  private _organizationListService = inject(OrganizationListService);
  private _conversationsService = inject(ConversationsService);
  private _connection = inject(FsConnectionService);
  private _sessionService = inject(SessionService);
  private _injector = inject(Injector);


  @ViewChild('sidenav', { static: true }) public sidenav: MatSidenav = null;
  @ViewChild('sideNavMainEl', { static: true }) public sideNavMainEl;

  @ViewChild('communicationsCount', { static: true })
  public communicationsCountTemplate: TemplateRef<any>;

  public disableClose: boolean;
  public sideNavMain: SidenavConfig = { items: [] };
  public sideNavExperience: SidenavConfig = { items: [] };
  public account: Account = null;
  public logoLink;
  public experience: Experience = null;
  public environments: Environment[] = [];
  public organization: Organization;

  // Unread conversations for the account, shown beside the Communications item
  // and — below the sidenav breakpoint — on the top bar's hamburger (TU-T9400).
  public readonly unreadCount = signal<number>(0);

  private _destroy$ = new Subject();

  // True while the Donations "+" action's organizations load is in flight —
  // guards double-taps from stacking Donate dialogs (TU-T9150).
  private readonly _donateBusy = signal(false);


  public ngOnInit(): void {
    this._bodyClassRenderer.addBodyClass('body-side-nav');
    this.disableClose = this.sidenavService.mode !== 'over';

    this.sidenavService.opened$
      .pipe(
        takeUntil(this._destroy$),
      )
      .subscribe((opened: boolean) => {
        if (opened && this.sidenavService.mode === 'over') {
          this._renderer.setStyle(this._document.documentElement, 'overflow', 'hidden');
        } else {
          this._renderer.removeStyle(this._document.documentElement, 'overflow');
        }
      });

    this.sidenavService.resize$
      .pipe(
        takeUntil(this._destroy$),
      )
      .subscribe(() => {
        this.disableClose = this.sidenavService.mode !== 'over';
        this._cdRef.markForCheck();
      });

    this.organization = this._organizationListService.getEnvironmentOrganization();

    this.sidenavService.changed$
      .pipe(
        takeUntil(this._destroy$),
      )
      .subscribe(() => {
        this._mainSideNavInit();
        this._buildSidenavExperience();
      });

    this._experienceService.experience$
      .pipe(
        takeUntil(this._destroy$),
      )
      .subscribe((experience: Experience) => {
        this.experience = experience;
        this.logoLink = this._isParentExperience() ? '/parent' : '/';
        this._mainSideNavInit();
        this._buildSidenavExperience();
      });

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this._destroy$),
      )
      .subscribe(() => {
        this.sidenavService.hide();
        this._revealOrganizationItems();
      });

    this.account = this._store.get('account');

    this._initUnreadCount();

    this._buildSidenavExperience();

    this._store.observe('environment')
      .pipe(
        skip(1),
        takeUntil(this._destroy$),
      )
      .subscribe(() => {
        this.organization = this._organizationListService.getEnvironmentOrganization();
        this._buildSidenavExperience();
      });

    this._store.observe('account')
      .pipe(
        skip(1),
        takeUntil(this._destroy$),
      )
      .subscribe((store) => {
        this.account = store.value;
      });
  }

  public clickNav(): void {
    this.sidenavService.hide();
  }

  public ngOnDestroy(): void {
    this._destroy$.next(null);
    this._destroy$.complete();
  }

  private _mainSideNavInit(): void {
    this._loadMainSidenav();
  }

  private _loadMainSidenav(): void {
    let items: Item[] = [];

    items = this._isParentExperience() ? [
      {
        label: 'Parent experience',
        class: 'experience-menu',
        actions: this._aclService.hasAnyOrganizationLevelPermissions() ? [
          {
            type: ItemActionType.Menu,
            icon: 'more_vert',
            menu: {
              component: ParentMenuComponent,
            },
          },
        ] : [],
      },
      {
        label: 'Students',
        path: '/parent/students',
        icon: 'school',
        exact: false,
      },
      {
        label: 'Enrollments',
        path: '/parent/enrollments',
        icon: 'list',
        exact: false,
      },
      {
        label: 'Schedule',
        path: '/parent/schedule',
        icon: 'event',
        exact: false,
      },
      {
        label: 'Registrations',
        path: '/parent/registrations',
        icon: 'description',
        exact: false,
      },
      {
        label: 'Invoices',
        path: '/parent/invoices',
        icon: 'receipt',
        exact: false,
      },
      {
        label: 'Payment methods',
        path: '/parent/paymentmethods',
        icon: 'credit_card',
        exact: false,
      },
      {
        label: 'Forms',
        path: '/parent/forms',
        icon: 'content_paste',
        exact: false,
      },
      {
        label: 'Donations',
        path: '/parent/donations',
        icon: 'attach_money',
        exact: false,
        actions: [
          {
            type: ItemActionType.Button,
            icon: 'add',
            // The sidenav action API has no disabled/busy affordance, so the
            // guard is click-side only: taps are ignored until the Donate
            // dialog opens (loaded) or the load fails (TU-T9150).
            click: () => {
              if (this._donateBusy()) {
                return;
              }

              this._donateBusy.set(true);

              this._donateService.donate({ loaded: () => this._donateBusy.set(false) })
                .pipe(
                  catchError(() => of(null)),
                  finalize(() => this._donateBusy.set(false)),
                  takeUntil(this._destroy$),
                )
                .subscribe();
            },
          },
        ],
      },
      {
        label: 'Communications',
        path: '/parent/inbox',
        icon: 'mail',
        exact: false,
        template: this.communicationsCountTemplate,
      },
    ] :
      [
        {
          label: 'Organization experience',
          class: 'experience-menu',
          actions: [
            {
              type: ItemActionType.Menu,
              icon: 'more_vert',
              menu: {
                component: OrganizationExperienceMenuComponent,
              },
            },
          ],
        },
        {
          label: 'Dashboard',
          path: '/dashboard',
          icon: 'dashboard',
          hide: !this._hasOrganizationExperienceSideNavItemPermissions(AclPermission.Invoicetransaction),
        },
        {
          label: 'Programs',
          path: '/programs',
          icon: 'assignment',
          exact: false,
          hide: !this._hasOrganizationExperienceSideNavItemPermissions(AclPermission.Program),
        },
        {
          label: 'Students',
          path: '/students',
          icon: 'school',
          exact: false,
          hide: !this._hasOrganizationExperienceSideNavItemPermissions(AclPermission.Studentparent),
        },
        {
          label: 'Enrollments',
          path: '/enrollments',
          icon: 'list',
          exact: false,
          hide: !this._hasOrganizationExperienceSideNavItemPermissions(AclPermission.Enrolment),
        },
        {
          label: 'Registrations',
          path: '/registrations',
          icon: 'description',
          exact: false,
          hide: !this._hasOrganizationExperienceSideNavItemPermissions(AclPermission.Registration),
        },
        {
          label: 'Parents',
          path: '/parents',
          icon: 'people',
          exact: false,
          hide: !this._hasOrganizationExperienceSideNavItemPermissions(AclPermission.Studentparent),
        },
        {
          label: 'Attendance',
          path: '/attendance',
          icon: 'assignment_turned_in',
          exact: false,
          hide: !this._hasOrganizationExperienceSideNavItemPermissions(AclPermission.Class)
            || !this._hasFeatureEnabled(FeatureFlag.Attendance),
        },
        {
          label: 'Invoices',
          path: '/invoices',
          icon: 'receipt',
          exact: false,
          hide: !this._hasOrganizationExperienceSideNavItemPermissions(AclPermission.Invoicetransaction),
        },
        {
          label: 'Forms',
          path: '/forms',
          icon: 'content_paste',
          exact: false,
          hide: !this._hasOrganizationExperienceSideNavItemPermissions(AclPermission.Form),
        },
        {
          label: 'Donations',
          path: '/donations',
          icon: 'attach_money',
          exact: false,
          hide: !this._hasOrganizationExperienceSideNavItemPermissions(AclPermission.Donation),
        },
        {
          label: 'Reports',
          path: '/reports',
          icon: 'bar_chart',
          exact: false,
          hide: !this._hasOrganizationExperienceSideNavItemPermissions(AllReportPermissions),
        },
        {
          label: 'Communications',
          path: '/conversations',
          icon: 'mail',
          exact: false,
          // Deliberately carries no `hide`. TU-T9400 requires this item for
          // every account — including one without the Conversation permission,
          // and an organization with Conversations turned off. It is the only
          // item in this menu that does not hide with its permission.
          template: this.communicationsCountTemplate,
        },
      ];

    this.sideNavMain = { items, itemClick: () => this.clickNav() };

    this._cdRef.markForCheck();
  }

  private _isParentExperience(): boolean {
    return this.experience === Experience.Parent;
  }

  /**
   * Moved verbatim from the top bar with TU-T9400, where the count used to sit
   * beside the envelope. Both writers are kept as they were: the poll is the
   * floor, the stats stream is what makes the count fall the moment a
   * conversation is read.
   */
  private _initUnreadCount(): void {
    timer(0, 15000)
      .pipe(
        filter(() => !!this._sessionService.account()),
        switchMap(() => this._conversationsService.conversationConfig
          .conversationsStats({
            account: true,
          }),
        ),
        takeUntil(this._destroy$),
      )
      .subscribe((data) => {
        this.unreadCount.set(data.account.unread);
      });

    this._conversationsService.unreadStats$
      .pipe(takeUntil(this._destroy$))
      .subscribe((data: any) => {
        this.unreadCount.set(data.account.unread);
      });

    // When the internet connection is restored, resync rather than waiting out
    // the poll interval.
    this._connection.up$
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this._conversationsService.triggerStatsUpdate();
      });

    this._conversationsService.conversationConfig.websocketService()
      .routeObservable(`account/${this.account.id}/unreadconversations`)
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this._conversationsService.triggerStatsUpdate();
      });
  }

  private _buildSidenavExperience(): void {
    let items: Item[] = [];

    if (!this._isParentExperience()) {
      const environmentItem = {
        label: 'Organization',
        class: 'experience-menu',
        actions: [],
      };

      const environment = this._store.get('environment');
      const hasSettingsAccess = environment ?
        this._aclService.hasWrite(AclPermission.Organization) : false;

      const organizationItem: any = {
        label: 'Not Selected',
        exact: true,
      };

      if (this._aclService.hasAnyOrganizationLevelPermissions()) {
        environmentItem.actions.push({
          type: ItemActionType.Menu,
          icon: 'more_vert',
          menu: {
            component: EnvironmentMenuComponent,
            data: { environment, hasSettingsAccess },
          },
        });
      }

      if (environment) {
        organizationItem.label = environment.name;
        organizationItem.image = environment.image.small;
        if (hasSettingsAccess) {
          organizationItem.path = `/organization/${environment.id}`;
          organizationItem.items = [
            {
              label: 'Settings', path: `/organization/${environment.id}/settings`,
              exact: false,
            },
            {
              label: 'Administrators', path: `/organization/${environment.id}/members`,
              exact: false,
            },
            {
              label: 'Discounts', path: `/organization/${environment.id}/discounts`,
              exact: false,
            },
            {
              label: 'Donations', path: `/organization/${environment.id}/donations`,
              exact: false,
            },
          ];
        }
      }

      items = items.concat([
        environmentItem,
        organizationItem,
      ]);
    }

    this.sideNavExperience = { items };
    this._cdRef.markForCheck();
  }

  /**
   * The organization sub-items open by route activation, below the fold on a
   * short viewport (TU-T9453). fs-sidenav's item directive adds `active-item`
   * in its own NavigationEnd subscription, after this one — hence the deferral.
   * The experience nav is the last content in the drawer, so aligning its
   * bottom edge reveals every sub-item.
   */
  private _revealOrganizationItems(): void {
    if (!this.router.url.startsWith('/organization/')) {
      return;
    }

    afterNextRender(() => {
      this.sideNavExperienceEl().nativeElement.scrollIntoView({ block: 'end' });
    }, { injector: this._injector });
  }

  /**
   * This function exists as a wraper for permissions check because possible
   * common sub rule for all rules like required organization:read for all items.
   */
  private _hasOrganizationExperienceSideNavItemPermissions(permission: AclPermission | AclPermission[]): boolean {
    return this._aclService.hasRead(permission);
  }

  private _hasFeatureEnabled(feature): boolean {
    return !!(this.organization?.featureFlags?.[feature]);
  }

}
